"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Copy, Send } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Badge } from "@/shared/ui/data-display/badge";
import { Button } from "@/shared/ui/buttons/button";
import {
  errorHandler,
  extractApiErrorMessage,
  type NormalizedApiError,
} from "@/shared/api/error-handler";
import { getAppEnvKind } from "@/shared/lib/app-env";
import { useUserProfile } from "@/hooks/useAuth";
import { isDeveloper as roleIsDeveloper } from "@/shared/lib/platform-roles";
import { api } from "@/lib/axiosClient";

const MAX_PAYLOAD_CHARS = 50_000;

export interface CrashReportErrorItem {
  timestamp: string | null;
  stage?: string;
  stageDescription?: string;
  method?: string;
  endpoint?: string;
  status?: number;
  code?: string;
  message?: string;
  location?: string | null;
  component?: string;
  action?: string;
}

export interface FullPayloadShape {
  reportedAt: string;
  userAgent: string;
  appEnv: string;
  url: string;
  errors: CrashReportErrorItem[];
}

function parseContext(context: string | undefined): { component?: string; action?: string } {
  if (!context) return {};
  try {
    const o = JSON.parse(context) as { component?: string; action?: string };
    return { component: o.component, action: o.action };
  } catch {
    return {};
  }
}

export function buildPayloadForReport(items: NormalizedApiError[]): string {
  const errors: CrashReportErrorItem[] = items.slice(0, 100).map((e) => {
    const ctx = parseContext(e.context);
    return {
      timestamp: e.timestamp ? new Date(e.timestamp).toISOString() : null,
      stage: e.stage,
      stageDescription: e.stageDescription,
      method: e.method,
      endpoint: e.endpoint,
      status: e.status,
      code: e.code,
      message: e.message,
      location: e.location ?? undefined,
      component: ctx.component,
      action: ctx.action,
    };
  });
  const payload: FullPayloadShape = {
    reportedAt: new Date().toISOString(),
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    appEnv: typeof window !== "undefined" ? (process.env["NEXT_PUBLIC_APP_ENV"] ?? process.env.NODE_ENV ?? "unknown") : "unknown",
    url: typeof window !== "undefined" ? window.location.href : "",
    errors,
  };
  const str = JSON.stringify(payload, null, 2);
  return str.length > MAX_PAYLOAD_CHARS ? str.slice(0, MAX_PAYLOAD_CHARS) : str;
}

function buildTextForCopy(items: NormalizedApiError[], single?: NormalizedApiError): string {
  const list = single ? [single] : items.slice(0, 200);
  return list
    .map((e) =>
      JSON.stringify({
        timestamp: e.timestamp ? new Date(e.timestamp).toISOString() : null,
        stage: e.stage,
        stageDescription: e.stageDescription,
        method: e.method,
        endpoint: e.endpoint,
        status: e.status,
        code: e.code,
        message: e.message,
        location: e.location,
        context: e.context,
        details: e.details,
      }),
    )
    .join(single ? "" : "\n");
}

function formatTime(ts: number | undefined): string {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toTimeString().slice(0, 8);
}

function dedupeByKey(items: NormalizedApiError[]): { item: NormalizedApiError; count: number }[] {
  const map = new Map<string, { item: NormalizedApiError; count: number }>();
  for (const e of items) {
    const key = `${e.endpoint ?? ""}|${e.status ?? ""}|${e.code ?? ""}`;
    const existing = map.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      map.set(key, { item: e, count: 1 });
    }
  }
  return Array.from(map.values());
}

const isDevEnv = () => {
  if (typeof window === "undefined") return false;
  return getAppEnvKind() !== "prod";
};

const COLLAPSED_ITEMS_COUNT = 3;

export function ErrorLogWidget() {
  const tToast = useTranslations("Toasts");
  const [items, setItems] = useState<NormalizedApiError[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [sending, setSending] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { data: user } = useUserProfile(false);
  // Виджет логов на проде доступен только роли «Разработчик» (Developer),
  // не SiteAdmin. Проверяем по platformRoles (роль может быть не основной).
  const isDeveloper = roleIsDeveloper(user);

  useEffect(() => {
    // 401/403 — не «сбой приложения», а нормальный ответ на «не залогинен / нет прав».
    // Раньше любой такой ответ поднимал плашку «Произошёл сбой, отправьте отчёт».
    const withoutAuthNoise = (list: NormalizedApiError[]) =>
      list.filter((e) => e.status !== 401 && e.status !== 403);
    setItems(withoutAuthNoise(errorHandler.getErrorLog()));
    const id = window.setInterval(() => {
      setItems(withoutAuthNoise(errorHandler.getErrorLog()));
    }, 3000);
    return () => window.clearInterval(id);
  }, []);

  const copyAll = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(buildTextForCopy(items));
      toast.success(tToast("errorLogWidget.oshibkiSkopirovany"));
    } catch {
      toast.error(tToast("errorLogWidget.neUdalosSkopirovat"));
    }
  }, [items, tToast]);

  const copyOne = useCallback(
    async (err: NormalizedApiError) => {
      try {
        await navigator.clipboard.writeText(buildTextForCopy(items, err));
        toast.success(tToast("errorLogWidget.oshibkaSkopirovana"));
      } catch {
        toast.error(tToast("errorLogWidget.neUdalosSkopirovat"));
      }
    },
    [items, tToast],
  );

  const sendReport = useCallback(async () => {
    if (items.length === 0) return;
    setSending(true);
    try {
      const payload = buildPayloadForReport(items);
      const title = items[0]?.message?.slice(0, 80) ?? "Отчёт о сбое";
      await api.post("/crash-reports", { title, fullPayload: payload });
      toast.success(tToast("errorLogWidget.otchetOtpravlenSpasibo"));
      errorHandler.clearErrorLog();
      setItems([]);
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "Отчёт не отправлен. Попробуйте ещё раз."));
    } finally {
      setSending(false);
    }
  }, [items, tToast]);

  const close = useCallback(() => {
    errorHandler.clearErrorLog();
    setItems([]);
  }, []);

  const grouped = useMemo(() => dedupeByKey(items), [items]);
  const displayedGroups = isExpanded ? grouped : grouped.slice(0, COLLAPSED_ITEMS_COUNT);
  const hasMoreItems = grouped.length > COLLAPSED_ITEMS_COUNT;

  // На проде debug-панель логов скрыта от всех, кроме роли «Разработчик».
  if (getAppEnvKind() === "prod" && !isDeveloper) return null;

  if (items.length === 0) return null;

  if (!isDeveloper) {
    return (
      <div className="pointer-events-none fixed bottom-4 left-4 z-60 w-96 max-w-[95vw] text-xs text-foreground">
        <Card className="pointer-events-auto border-destructive/40 bg-background/95 shadow-lg">
          <CardHeader className="py-2 px-3">
            <CardTitle className="text-xs font-semibold">Произошёл сбой приложения</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pb-3">
            <p className="text-muted-foreground text-[11px]">
              Мы зафиксировали ошибку. Отправьте отчёт разработчикам — это поможет исправить проблему.
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="flex-1"
                onClick={sendReport}
                disabled={sending}
              >
                <Send className="mr-2 h-3.5 w-3.5" />
                {sending ? "Отправка…" : "Отправить отчёт о сбое"}
              </Button>
              <Button size="sm" variant="outline" onClick={close}>
                Закрыть
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="pointer-events-none fixed bottom-4 left-4 z-60 min-w-0 w-[420px] max-w-[95vw] text-xs text-foreground"
    >
      <Card className="pointer-events-auto min-w-0 border-destructive/40 bg-background/95 shadow-lg overflow-hidden">
        <CardHeader className="py-2 px-3 min-w-0">
          <div className="flex items-center justify-between gap-2 min-w-0">
            <CardTitle className="text-xs font-semibold min-w-0 truncate">
              {isDevEnv() ? "Логи ошибок (dev)" : "Логи ошибок"}
            </CardTitle>
            <div className="flex items-center gap-1">
              <Badge variant="outline" className="text-[10px]">
                {items.length}
              </Badge>
              {hasMoreItems && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="h-7 px-2"
                  title={isExpanded ? "Свернуть" : "Развернуть"}
                >
                  {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={copyAll} className="h-7 px-2" title="Скопировать всё">
                <Copy className="h-3 w-3" />
              </Button>
              <Button variant="outline" size="sm" onClick={close} className="h-7 px-2">
                Закрыть
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-2 min-w-0 overflow-hidden">
          <ScrollArea
            className={`min-w-0 px-3 ${isExpanded ? "h-[500px] max-h-[70vh]" : "max-h-[300px]"}`}
          >
            <div className="space-y-2 pr-4 min-w-0">
              {displayedGroups.map(({ item: err, count }, idx) => {
                const key = `${err.endpoint ?? ""}-${err.status ?? ""}-${err.code ?? ""}-${idx}`;
                const isItemExpanded = Boolean(expandedItems[key]);
                const hasDetails = err.context != null || err.details != null;
                const methodUrl = [err.method, err.endpoint].filter(Boolean).join(" ");
                return (
                  <div
                    key={key}
                    className="min-w-0 rounded border border-destructive/30 bg-destructive/5 p-2 overflow-hidden"
                  >
                    <div className="flex items-center justify-between gap-2 min-w-0">
                      <span className="min-w-0 flex-1 overflow-hidden text-[10px] text-muted-foreground break-all">
                        {methodUrl || "—"} {err.status != null && `• ${err.status}`}
                      </span>
                      <div className="flex shrink-0 items-center gap-1">
                        <span className="text-[10px] text-muted-foreground">
                          {formatTime(err.timestamp)}
                        </span>
                        {count > 1 && (
                          <Badge variant="secondary" className="text-[9px] px-1">
                            ×{count}
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => copyOne(err)}
                          title="Скопировать эту ошибку"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-1 break-all text-[11px] font-medium overflow-hidden">
                      {err.stageDescription ?? err.message}
                    </div>
                    <div className="mt-0.5 break-all text-[10px] text-muted-foreground overflow-hidden">
                      {err.message}
                    </div>
                    {err.location && (
                      <div className="mt-0.5 break-all text-[10px] text-muted-foreground overflow-hidden">
                        {err.location}
                      </div>
                    )}
                    {hasDetails && (
                      <button
                        type="button"
                        className="mt-2 text-[10px] text-muted-foreground underline-offset-2 hover:underline"
                        onClick={() =>
                          setExpandedItems((prev) => ({ ...prev, [key]: !prev[key] }))
                        }
                      >
                        {isItemExpanded ? "Свернуть" : "Подробнее ▾"}
                      </button>
                    )}
                    {isItemExpanded && (
                      <div className="mt-2 min-w-0 max-h-[200px] overflow-auto rounded-md border border-border/60 bg-muted/40 p-2 text-[10px] leading-relaxed text-muted-foreground">
                        {err.context != null && (
                          <pre className="whitespace-pre-wrap break-all font-sans text-[10px]">{String(err.context)}</pre>
                        )}
                        {err.details != null && (
                          <pre className="mt-2 whitespace-pre-wrap break-all font-sans text-[10px]">
                            {typeof err.details === "string"
                              ? err.details
                              : JSON.stringify(err.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {!isExpanded && hasMoreItems && (
                <div className="py-2 text-center text-[10px] text-muted-foreground">
                  +{grouped.length - COLLAPSED_ITEMS_COUNT} ошибок
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
